import { computed, inject, Injector, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
  withFeature,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';

import {
  SubmissionTypesAdminSignalState,
  AdminWorkspaceSubTypeV2Schema,
} from '../../types';
import { SubmissionTypesService } from '../../services/submission-types.service';
import {
  SUBMISSION_TYPES_INITIAL_STATE,
  SUBMISSION_TYPES_INITIAL_STATE_TOKEN,
} from '../../constants/submission-types.constant';
import { SubmissionService } from '@data-access/configurations';
import { ShallowCamelCasePipe } from '@shared/pipes/shallow-camel-case.pipe';
import { GridType } from '@data-access/grid-views';
import {
  LoadGridPayload,
  transformGridDateFormat,
  withGridConfigFeatures,
} from '@data-access/shared';
import { withSubmissionTypeEntities } from './submission-types.entities';
import { BuildLinkFromValue } from '@wcg/frontend-framework';

/**
 * Signal store for managing submission admin state including workspace sub-types search and filters
 */
export const submissionTypesSignalStore = signalStore(
  withState(() => inject(SUBMISSION_TYPES_INITIAL_STATE_TOKEN)),
  withProps(
    (store, _submissionTypesAdminService = inject(SubmissionTypesService)) => ({
      _injector: inject(Injector),
      _submissionTypesAdminService,
      _submissionService: inject(SubmissionService),
      store,
      _shallowCamelCasePipe: new ShallowCamelCasePipe(),
      _buildLinkFromValue: new BuildLinkFromValue(),
    }),
  ),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withSubmissionTypeEntities()),
  withComputed(
    ({
      total,
      isLoading,
      gridType,
      error,
      store,
      _submissiontypesEntities,
      _injector,
    }) => ({
      $workspaceSubTypes: computed(() => {
        const _submissionTypes = _submissiontypesEntities();
        if (!_submissionTypes || _submissionTypes.length === 0) {
          return [] as AdminWorkspaceSubTypeV2Schema[]; // Return an empty array if no data
        }
        return transformGridDateFormat<AdminWorkspaceSubTypeV2Schema>(
          _injector,
          _submissionTypes,
          store.columnsCopy(),
        );
      }),
      $isLoading: computed(() => isLoading()),
      $error: computed(() => error()),
      $gridType: computed(() => {
        const _gridType: GridType = gridType();
        return _gridType;
      }),
      $totalCount: computed(() => total()),
      $selectedWorkspaceSubType: computed(() =>
        store.selectedWorkspaceSubType(),
      ),
      $irisRoutingGroups: computed(() => store.irisRoutingGroupData.data()),
    }),
  ),
  withMethods(
    ({
      _submissionTypesAdminService,
      _submissionService,
      _shallowCamelCasePipe,
      _buildLinkFromValue,
      ...store
    }) => {
      /**
       * Load workspace sub types with HttpParams from wcgTableUtils
       */
      const _loadWorkspaceSubTypes = rxMethod<LoadGridPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ params, updateColumns }) =>
            _submissionTypesAdminService.listWorkspaceSubTypes(params).pipe(
              map((response) => ({
                ...response,
                config: _shallowCamelCasePipe.transform(response.config),
              })),

              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error: error,
                    isLoading: false,
                  }),
                finalize: () =>
                  patchState(store, {
                    isLoading: false,
                  }),
                next: (response) => {
                  patchState(store, {
                    currentStateDestroyed: false,
                    total: response.total,
                  });
                  store.loadSubmissionTypes(response.data);
                  store.loadConfigurations(
                    response.config,
                    updateColumns,
                    response.data?.[0],
                    _buildLinkFromValue,
                  );
                  store.setGridViews(response.views);
                },
              }),
            ),
          ),
        ),
      );

      /**
       * Load a specific workspace type by ID
       */
      const _loadWorkspaceTypeById = rxMethod<{
        workspaceTypeId: string;
        entityType?: string;
      }>(
        pipe(
          tap(() => {
            patchState(store, {
              isLoading: true,
              error: null,
            });
          }),
          switchMap(({ workspaceTypeId, entityType }) =>
            _submissionTypesAdminService
              .getWorkspaceTypeById(workspaceTypeId, entityType)
              .pipe(
                tapResponse({
                  next: (workspaceType: AdminWorkspaceSubTypeV2Schema) => {
                    patchState(store, {
                      selectedWorkspaceSubType: workspaceType,
                      isLoading: false,
                    });
                  },
                  error: (error: Error) => {
                    patchState(store, {
                      error,
                      isLoading: false,
                      selectedWorkspaceSubType: null,
                    });
                  },
                }),
              ),
          ),
        ),
      );

      /**
       * Load Iris Routing Groups
       */
      const _getIrisRoutingGroups = rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() =>
            _submissionService.getIrisRoutingGroups().pipe(
              tapResponse({
                error: (error: Error) => patchState(store, { error: error }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (routingGroups) =>
                  patchState(store, { irisRoutingGroupData: routingGroups }),
              }),
            ),
          ),
        ),
      );

      return {
        /**
         * Load workspace sub types with HttpParams
         * This should be called from the component with params generated by wcgTableUtils.generateTableQueryParams()
         */
        loadWorkspaceSubTypes: (params: LoadGridPayload) =>
          _loadWorkspaceSubTypes(params),

        /**
         * Load a specific workspace type by ID
         */
        loadWorkspaceTypeById: (workspaceTypeId: string, entityType?: string) =>
          _loadWorkspaceTypeById({ workspaceTypeId, entityType }),

        /**
         * Reset grid settings
         */
        resetGridSettings: (payload: LoadGridPayload) => {
          _loadWorkspaceSubTypes(payload);
          patchState(store, { searchString: '' });
          store.setDefaultGridView();
        },

        /**
         * Reset to initial state
         */
        resetState: () =>
          patchState(store, { ...SUBMISSION_TYPES_INITIAL_STATE }),

        loadIrisRoutingGroups: () => _getIrisRoutingGroups(),
      };
    },
  ),
) satisfies Type<SubmissionTypesAdminSignalState>;
